import { useEffect, useMemo, useState } from 'react';
import type { FunctionComponent, SVGProps } from 'react';
import { Modal } from 'antd';
import * as XLSX from 'xlsx';
import type { AcOption } from './DefineBoundaryStep';
import {
  SearchIcon,
  CaretIcon,
  CheckmarkIcon,
  ChevronRightIcon,
  FilterIcon,
  ColumnsIcon,
  KebabIcon,
  ArrowRightIcon,
  LinkIcon,
  TrashIcon,
  TemplateDocIcon,
  BulkUploadIcon,
  NodeAlwarIcon,
  NodeMandalsIcon,
  NodeMandalIcon,
  NodeSkendrasIcon,
  NodeSkendraIcon,
  NodeBoothsIcon,
  NodeBoothIcon,
} from '../../../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

/* ------------------------------------------------------------------ *
 * Source tree (Internal Organization)
 * ------------------------------------------------------------------ */

interface SourceNode {
  id: string;
  label: string;
  Icon: IconType;
  iconClass: string; // color for the node glyph
  children?: SourceNode[];
}

const SOURCE_TREE: SourceNode[] = [
  {
    id: 'alwar',
    label: 'Alwar',
    Icon: NodeAlwarIcon,
    iconClass: 'text-brand',
    children: [
      {
        id: 'mandals',
        label: 'Mandals',
        Icon: NodeMandalsIcon,
        iconClass: 'text-brand',
        children: [
          {
            id: 'mandal1',
            label: 'Mandal 1',
            Icon: NodeMandalIcon,
            iconClass: 'text-[#bc4800]',
            children: [
              {
                id: 'skendras',
                label: 'Shakti Kendras',
                Icon: NodeSkendrasIcon,
                iconClass: 'text-[#40484f]',
                children: [
                  {
                    id: 'skendra1',
                    label: 'Shakti Kendra 1',
                    Icon: NodeSkendraIcon,
                    iconClass: 'text-[#40484f]',
                    children: [
                      {
                        id: 'booths',
                        label: 'Booths',
                        Icon: NodeBoothsIcon,
                        iconClass: 'text-[#585f67]',
                        children: [
                          { id: 'booth1', label: 'Booth 1', Icon: NodeBoothIcon, iconClass: 'text-brand' },
                          { id: 'booth2', label: 'Booth 2', Icon: NodeBoothIcon, iconClass: 'text-[#585f67]' },
                          { id: 'booth3', label: 'Booth 3', Icon: NodeBoothIcon, iconClass: 'text-[#585f67]' },
                        ],
                      },
                    ],
                  },
                  {
                    id: 'skendra2',
                    label: 'Shakti Kendra 2',
                    Icon: NodeSkendraIcon,
                    iconClass: 'text-[#40484f]',
                  },
                ],
              },
              { id: 'mandal2', label: 'Mandal 2', Icon: NodeMandalIcon, iconClass: 'text-[#bc4800]' },
            ],
          },
        ],
      },
    ],
  },
];

interface MapEntry {
  sourceId: string;
  sourceLabel: string;
  boothLabel: string;
}

function SourceTree({
  mapped,
  onUnlink,
}: {
  mapped: Record<string, string[]>;
  onUnlink: (sourceId: string, boothLabel: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(['alwar', 'mandals', 'mandal1', 'skendras', 'skendra1', 'booths']),
  );
  const [selected, setSelected] = useState('booth1');

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: SourceNode) => {
    const mappedTo = mapped[node.id] ?? [];
    const hasReal = Boolean(node.children?.length);
    const hasChildren = hasReal || mappedTo.length > 0;
    // Auto-open nodes that have mapped booths so they're visible immediately.
    const open = expanded.has(node.id) || mappedTo.length > 0;
    const isSelected = selected === node.id;
    return (
      <div key={node.id}>
        <button
          type="button"
          onClick={() => (hasChildren ? toggle(node.id) : setSelected(node.id))}
          className={[
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-semibold tracking-[0.24px] transition-colors',
            isSelected ? 'bg-[rgba(0,74,198,0.1)] text-brand' : 'text-ink hover:bg-brand-shade/60',
          ].join(' ')}
        >
          {hasChildren ? (
            <CaretIcon
              className={['size-3 shrink-0 text-ink-secondary', open ? '' : '-rotate-90'].join(' ')}
            />
          ) : (
            <span className="size-3 shrink-0" />
          )}
          <node.Icon className={['size-4 shrink-0', node.iconClass].join(' ')} />
          <span className="truncate">{node.label}</span>
          {mappedTo.length > 0 && (
            <span className="ml-auto shrink-0 rounded bg-[#dcfce7] px-1.5 py-0.5 text-[10px] font-medium text-[#166534]">
              {mappedTo.length} mapped
            </span>
          )}
        </button>
        {open && (
          <div className="ml-[13px] flex flex-col gap-1 border-l border-[rgba(195,198,215,0.5)] pl-3">
            {node.children?.map(renderNode)}
            {mappedTo.map((boothLabel, i) => (
              <div
                key={`${node.id}-map-${i}`}
                className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-xs"
              >
                <span className="size-3 shrink-0" />
                <LinkIcon className="size-3.5 shrink-0 text-[#16a34a]" />
                <span className="truncate font-medium text-[#166534]">{boothLabel}</span>
                <button
                  type="button"
                  aria-label={`Unlink ${boothLabel}`}
                  onClick={() => onUnlink(node.id, boothLabel)}
                  className="ml-auto flex size-5 shrink-0 items-center justify-center rounded text-[#94a3b8] transition-colors hover:bg-red-50 hover:text-red-500"
                >
                  <TrashIcon className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return <div className="flex flex-col gap-1">{SOURCE_TREE.map(renderNode)}</div>;
}

/* ------------------------------------------------------------------ *
 * Destination miller-columns (Election Commission Structure)
 * ------------------------------------------------------------------ */

interface DestNode {
  id: string;
  label: string;
  children?: DestNode[];
}

/** Rural sub-hierarchy for one constituency (ids prefixed so multiple ACs don't collide). */
function ruralSubtree(p: string): DestNode {
  return {
    id: `${p}-rural`,
    label: 'Rural',
    children: [
      {
        id: `${p}-ps-bansur`,
        label: 'Bansur Panchayat Samiti',
        children: [
          {
            id: `${p}-gp-bansur`,
            label: 'Bansur Gram Panchayat',
            children: [
              {
                id: `${p}-vil-bansur`,
                label: 'Bansur Village',
                children: [
                  { id: `${p}-booth-r1`, label: 'Booth 1 — Govt. School' },
                  { id: `${p}-booth-r2`, label: 'Booth 2 — Panchayat Bhawan' },
                ],
              },
            ],
          },
        ],
      },
      {
        id: `${p}-ps-kishangarh`,
        label: 'Kishangarh Bas Panchayat Samiti',
        children: [
          {
            id: `${p}-gp-kishangarh`,
            label: 'Kishangarh Gram Panchayat',
            children: [
              {
                id: `${p}-vil-kishangarh`,
                label: 'Kishangarh Village',
                children: [
                  { id: `${p}-booth-r3`, label: 'Booth 3 — Community Hall' },
                  { id: `${p}-booth-r4`, label: 'Booth 4 — Primary School' },
                ],
              },
            ],
          },
        ],
      },
      {
        id: `${p}-ps-ramgarh`,
        label: 'Ramgarh Panchayat Samiti',
        children: [
          {
            id: `${p}-gp-ramgarh`,
            label: 'Ramgarh Gram Panchayat',
            children: [
              {
                id: `${p}-vil-ramgarh`,
                label: 'Ramgarh Village',
                children: [{ id: `${p}-booth-r5`, label: 'Booth 5 — Anganwadi Centre' }],
              },
            ],
          },
        ],
      },
      {
        id: `${p}-ps-thanagazi`,
        label: 'Thanagazi Panchayat Samiti',
        children: [
          {
            id: `${p}-gp-thanagazi`,
            label: 'Thanagazi Gram Panchayat',
            children: [
              {
                id: `${p}-vil-thanagazi`,
                label: 'Thanagazi Village',
                children: [{ id: `${p}-booth-r6`, label: 'Booth 6 — Health Centre' }],
              },
            ],
          },
        ],
      },
    ],
  };
}

/** Urban sub-hierarchy for one constituency. */
function urbanSubtree(p: string): DestNode {
  return {
    id: `${p}-urban`,
    label: 'Urban',
    children: [
      {
        id: `${p}-mun-1`,
        label: 'Municipal Council',
        children: [
          {
            id: `${p}-ward-1`,
            label: 'Ward 1',
            children: [
              { id: `${p}-booth-u1`, label: 'Booth 1 — Municipal Office' },
              { id: `${p}-booth-u2`, label: 'Booth 2 — City Library' },
            ],
          },
          {
            id: `${p}-ward-2`,
            label: 'Ward 2',
            children: [{ id: `${p}-booth-u3`, label: 'Booth 3 — Town Hall' }],
          },
        ],
      },
      {
        id: `${p}-mun-2`,
        label: 'Nagar Palika',
        children: [
          {
            id: `${p}-ward-5`,
            label: 'Ward 5',
            children: [{ id: `${p}-booth-u4`, label: 'Booth 4 — Community Centre' }],
          },
        ],
      },
    ],
  };
}

/** A constituency (Level 1) node with its Rural + Urban sub-trees. */
function acDest(id: string, label: string): DestNode {
  return { id, label, children: [ruralSubtree(id), urbanSubtree(id)] };
}

/** The full source structure inside the mapping popup — drill and select any unit. */
function SourceMapTree({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (id: string, label: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(['alwar', 'mandals', 'mandal1', 'skendras', 'skendra1', 'booths']),
  );
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: SourceNode, depth: number) => {
    const hasChildren = Boolean(node.children?.length);
    const open = expanded.has(node.id);
    const selected = selectedId === node.id;
    return (
      <div key={node.id}>
        <div
          className={[
            'flex items-center gap-2 rounded-md py-1.5 pr-2 text-xs transition-colors',
            selected ? 'bg-[rgba(0,74,198,0.1)]' : 'hover:bg-brand-shade/60',
          ].join(' ')}
          style={{ paddingLeft: depth * 16 + 8 }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggle(node.id)}
              className="flex size-4 shrink-0 items-center justify-center text-ink-secondary"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              <CaretIcon className={['size-3', open ? '' : '-rotate-90'].join(' ')} />
            </button>
          ) : (
            <span className="size-4 shrink-0" />
          )}
          <button
            type="button"
            onClick={() => onSelect(node.id, node.label)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <node.Icon className={['size-4 shrink-0', node.iconClass].join(' ')} />
            <span
              className={['truncate', selected ? 'font-semibold text-brand' : 'text-ink'].join(' ')}
            >
              {node.label}
            </span>
          </button>
          {selected && <CheckmarkIcon className="size-4 shrink-0 text-brand" />}
        </div>
        {hasChildren && open && node.children!.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className="max-h-[360px] overflow-auto">{SOURCE_TREE.map((n) => renderNode(n, 0))}</div>
  );
}

function DestCheckbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className={[
        'flex size-4 shrink-0 items-center justify-center rounded-sm transition-colors',
        checked ? 'bg-brand text-white' : 'border-2 border-line bg-white',
      ].join(' ')}
    >
      {checked && <CheckmarkIcon className="size-3" />}
    </button>
  );
}

function DestinationColumns({
  tree,
  branches,
  mapping,
  setMapping,
}: {
  tree: DestNode[];
  branches?: Record<string, 'urban' | 'rural'>;
  mapping: Record<string, MapEntry>;
  setMapping: React.Dispatch<React.SetStateAction<Record<string, MapEntry>>>;
}) {
  const firstAc = tree[0]?.id ?? '';
  const firstBranch = branches?.[firstAc];
  const [path, setPath] = useState<string[]>(
    firstBranch ? [firstAc, `${firstAc}-${firstBranch}`] : [firstAc],
  );
  const [linkingBooth, setLinkingBooth] = useState<DestNode | null>(null);
  const [mapSel, setMapSel] = useState<{ id: string; label: string } | null>(null);

  const openMap = (booth: DestNode) => {
    setLinkingBooth(booth);
    setMapSel(null);
  };
  const closeMap = () => {
    setLinkingBooth(null);
    setMapSel(null);
  };
  const unlinkBooth = (boothId: string) =>
    setMapping((m) => {
      const next = { ...m };
      delete next[boothId];
      return next;
    });

  // Reset to the first constituency (and its branch, if any) when the selection changes.
  useEffect(() => {
    const f = tree[0]?.id ?? '';
    const b = branches?.[f];
    setPath(b ? [f, `${f}-${b}`] : [f]);
  }, [branches, tree]);

  /**
   * Single-select per column: `path` holds at most one selected id per level.
   * Selecting a row checks it (clearing its siblings + any deeper selection) and,
   * if it has children, drills into it. Clicking the selected row again clears it.
   */
  const select = (colIndex: number, id: string) =>
    setPath((prev) =>
      prev[colIndex] === id ? prev.slice(0, colIndex) : [...prev.slice(0, colIndex), id],
    );

  // Build columns + breadcrumb labels from the drilled path.
  const columns: DestNode[][] = [tree];
  const crumbs: string[] = [];
  let cursor: DestNode[] = tree;
  for (let i = 0; i < path.length; i += 1) {
    const node = cursor.find((n) => n.id === path[i]);
    if (!node) break;
    crumbs.push(node.label);
    if (node.children?.length) {
      columns.push(node.children);
      cursor = node.children;
    } else break;
  }

  // Level 2 (Rural/Urban): if this constituency picked a branch in step 2, show
  // only that option; otherwise show both. Keyed by the drilled AC (path[0]).
  const drilledAcBranch = branches?.[path[0]];
  if (drilledAcBranch && columns[1]) {
    columns[1] = columns[1].filter((n) => n.id.endsWith(`-${drilledAcBranch}`));
  }

  return (
    <>
      {/* Breadcrumb path */}
      <div className="flex items-center gap-2 overflow-auto border-b border-line bg-[#eff4ff] px-4 py-3">
        {crumbs.map((label, i) => (
          <span key={`${label}-${i}`} className="flex shrink-0 items-center gap-2">
            <span
              className={[
                'text-[11px] font-medium tracking-[0.55px]',
                i === crumbs.length - 1 ? 'text-ink' : 'text-brand',
              ].join(' ')}
            >
              {label}
            </span>
            {i < crumbs.length - 1 && (
              <ChevronRightIcon className="h-[7px] w-[4px] text-ink-secondary" />
            )}
          </span>
        ))}
      </div>

      {/* Columns */}
      <div className="flex-1 overflow-x-auto bg-[#f8f9ff]">
        <div className="flex h-full min-w-full">
          {columns.map((items, colIndex) => (
            <div
              key={colIndex}
              className="flex w-[280px] shrink-0 flex-col border-r border-line last:border-r-0"
            >
              <div className="border-b border-line bg-[rgba(229,238,255,0.3)] px-4 pb-3 pt-3 text-xs font-semibold uppercase tracking-[0.6px] text-ink">
                Level {colIndex + 1}
              </div>
              <div className="flex flex-1 flex-col gap-1 overflow-auto p-2">
                {items.map((node) => {
                  const hasChildren = Boolean(node.children?.length);
                  const isBooth = node.id.includes('booth');
                  const active = path[colIndex] === node.id;
                  return (
                    <div
                      key={node.id}
                      onClick={() => select(colIndex, node.id)}
                      className={[
                        'flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-2 text-left transition-colors',
                        active
                          ? 'border border-[rgba(0,74,198,0.2)] bg-[rgba(0,74,198,0.1)]'
                          : 'border border-transparent hover:bg-brand-shade/50',
                      ].join(' ')}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {!isBooth && (
                          <DestCheckbox checked={active} onChange={() => select(colIndex, node.id)} />
                        )}
                        <span
                          className={[
                            'truncate text-[13px]',
                            active ? 'font-semibold text-[#00174b]' : 'font-normal text-ink',
                          ].join(' ')}
                        >
                          {node.label}
                        </span>
                      </span>

                      {isBooth ? (
                        <span
                          className="flex shrink-0 items-center gap-1.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {mapping[node.id] && (
                            <span className="rounded bg-[#dcfce7] px-1.5 py-0.5 text-[10px] font-medium text-[#166534]">
                              {mapping[node.id].sourceLabel}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => openMap(node)}
                            className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-semibold text-brand transition-colors hover:bg-brand-shade"
                          >
                            <LinkIcon className="size-3" />
                            {mapping[node.id] ? 'Linked' : 'Link'}
                          </button>
                          {mapping[node.id] && (
                            <button
                              type="button"
                              aria-label="Unlink"
                              onClick={() => unlinkBooth(node.id)}
                              className="flex size-6 items-center justify-center rounded text-[#94a3b8] transition-colors hover:bg-red-50 hover:text-red-500"
                            >
                              <TrashIcon className="size-3.5" />
                            </button>
                          )}
                        </span>
                      ) : (
                        hasChildren && (
                          <ChevronRightIcon className="h-[10px] w-[6px] shrink-0 text-ink-secondary" />
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Map-booth popup */}
      <Modal
        open={Boolean(linkingBooth)}
        onCancel={closeMap}
        footer={null}
        width={560}
        title={linkingBooth ? `Map "${linkingBooth.label}"` : ''}
      >
        <p className="mb-3 text-sm text-ink-secondary">
          Select a unit from the Internal Organization structure to map to this booth.
        </p>
        <div className="rounded-lg border border-line p-2">
          <SourceMapTree
            selectedId={mapSel?.id ?? null}
            onSelect={(id, label) => setMapSel({ id, label })}
          />
        </div>
        {mapSel && (
          <p className="mt-3 text-sm text-ink-secondary">
            Mapping to <span className="font-semibold text-ink">{mapSel.label}</span>
          </p>
        )}
        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={closeMap}
            className="rounded-lg border border-line px-5 py-2 text-sm font-medium text-ink-secondary transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!mapSel}
            onClick={() => {
              if (linkingBooth && mapSel) {
                setMapping((m) => ({
                  ...m,
                  [linkingBooth.id]: {
                    sourceId: mapSel.id,
                    sourceLabel: mapSel.label,
                    boothLabel: linkingBooth.label,
                  },
                }));
              }
              closeMap();
            }}
            className="rounded-lg bg-brand px-6 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark disabled:opacity-40"
          >
            Map
          </button>
        </div>
      </Modal>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Info cards
 * ------------------------------------------------------------------ */

function InfoCard({
  Icon,
  iconClass,
  badgeClass,
  title,
  subtitle,
  action,
}: {
  Icon: IconType;
  iconClass: string;
  badgeClass: string;
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex flex-1 items-center justify-between gap-4 rounded-xl border border-line bg-white p-6 shadow-card">
      <div className="flex items-center gap-4">
        <span
          className={['flex size-12 shrink-0 items-center justify-center rounded-full', badgeClass, iconClass].join(' ')}
        >
          <Icon className="h-5 w-4" />
        </span>
        <div className="flex flex-col">
          <span className="text-base font-semibold text-ink">{title}</span>
          <span className="text-[13px] text-ink-secondary">{subtitle}</span>
        </div>
      </div>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Step
 * ------------------------------------------------------------------ */

const PILL = 'rounded bg-[#dce9ff] px-2 py-1 text-[11px] font-medium uppercase tracking-[0.55px] text-ink';

const FALLBACK_LEVELS = ['Alwar BJP', 'Mandal', 'Shakti Kendra', 'Booths'];

/** Step 4 — Organization Hierarchy Data Import. */
export default function DataImportStep({
  onConfirm,
  hierarchyLevels,
  branches,
  constituencies,
}: {
  onConfirm: () => void;
  hierarchyLevels?: string[];
  branches?: Record<string, 'urban' | 'rural'>;
  constituencies?: AcOption[];
}) {
  // Level 1 = the constituencies chosen in step 2 (fallback to a sample).
  const destTree = useMemo(() => {
    const acs =
      constituencies && constituencies.length > 0
        ? constituencies
        : [{ id: 'alwar', label: 'Alwar AC' }];
    return acs.map((a) => acDest(a.id, a.label));
  }, [constituencies]);

  // Shared booth→source mapping (keyed by destination booth id). Seeded with a
  // couple of sample links so the mapped state is visible on both sides for testing.
  const firstAcId = destTree[0]?.id ?? 'alwar';
  const [mapping, setMapping] = useState<Record<string, MapEntry>>({
    [`${firstAcId}-booth-r1`]: {
      sourceId: 'booth1',
      sourceLabel: 'Booth 1',
      boothLabel: 'Booth 1 — Govt. School',
    },
    [`${firstAcId}-booth-u1`]: {
      sourceId: 'booth2',
      sourceLabel: 'Booth 2',
      boothLabel: 'Booth 1 — Municipal Office',
    },
  });

  // Reverse index: source node id → destination booth labels mapped to it.
  const mappedBySource = useMemo(() => {
    const rev: Record<string, string[]> = {};
    Object.values(mapping).forEach((e) => {
      (rev[e.sourceId] = rev[e.sourceId] ?? []).push(e.boothLabel);
    });
    return rev;
  }, [mapping]);

  // Unlink from the left tree: drop any mapping entry matching this source + booth.
  const unlinkFromSource = (sourceId: string, boothLabel: string) =>
    setMapping((m) => {
      const next = { ...m };
      Object.keys(next).forEach((k) => {
        if (next[k].sourceId === sourceId && next[k].boothLabel === boothLabel) delete next[k];
      });
      return next;
    });

  // Build & download an .xlsx whose column headers are the ordered levels from
  // step 3 (Alwar BJP, Mandal, …), with empty rows below for entering values.
  const downloadTemplate = () => {
    const levels = hierarchyLevels && hierarchyLevels.length > 0 ? hierarchyLevels : FALLBACK_LEVELS;
    const emptyRows = Array.from({ length: 20 }, () => levels.map(() => ''));
    const ws = XLSX.utils.aoa_to_sheet([levels, ...emptyRows]);
    ws['!cols'] = levels.map(() => ({ wch: 24 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hierarchy');
    XLSX.writeFile(wb, 'organization-hierarchy-template.xlsx');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Info cards */}
      <div className="flex flex-col gap-4 lg:flex-row">
        <InfoCard
          Icon={TemplateDocIcon}
          iconClass="text-brand"
          badgeClass="bg-[#cdd4ff]"
          title="Hierarchy Template"
          subtitle="Download the standardized structure"
          action={
            <button
              type="button"
              onClick={downloadTemplate}
              className="shrink-0 rounded-lg bg-[#dce3ec] px-4 py-2 text-xs font-semibold tracking-[0.24px] text-[#5e656d] transition-colors hover:brightness-95"
            >
              Download XLSX
            </button>
          }
        />
        <InfoCard
          Icon={BulkUploadIcon}
          iconClass="text-[#943700]"
          badgeClass="bg-[#ffdbcd]"
          title="Bulk Upload Hierarchy"
          subtitle="Import multiple organization levels"
          action={
            <button
              type="button"
              className="shrink-0 rounded-lg bg-brand px-4 py-2 text-xs font-semibold tracking-[0.24px] text-white transition-colors hover:bg-brand-dark"
            >
              Select Files
            </button>
          }
        />
      </div>

      {/* Explorer */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        {/* SOURCE */}
        <div className="flex h-[640px] flex-col overflow-hidden rounded-xl bg-white shadow-card lg:w-[400px]">
          <div className="flex items-center justify-between border-b border-line bg-[rgba(229,238,255,0.5)] px-4 py-4">
            <div className="flex items-center gap-3">
              <span className={PILL}>Source</span>
              <h2 className="text-base font-semibold text-ink">Internal Organization</h2>
            </div>
            <button type="button" aria-label="Filter" className="text-ink">
              <FilterIcon className="h-[10px] w-[15px]" />
            </button>
          </div>

          <div className="border-b border-line px-4 py-4">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-[13.5px] -translate-y-1/2 text-ink-secondary/70" />
              <input
                type="search"
                placeholder="Search units..."
                className="w-full rounded-lg bg-[#e5eeff] py-2.5 pl-10 pr-4 text-[13px] text-ink placeholder:text-ink-secondary/70 focus:outline-none focus:ring-2 focus:ring-brand/15"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4">
            <SourceTree mapped={mappedBySource} onUnlink={unlinkFromSource} />
          </div>
        </div>

        {/* DESTINATION */}
        <div className="flex h-[640px] flex-col overflow-hidden rounded-xl bg-white shadow-card lg:flex-1">
          <div className="flex items-center justify-between border-b border-line bg-[rgba(229,238,255,0.5)] px-4 py-4">
            <div className="flex items-center gap-3">
              <span className={PILL}>Destination</span>
              <h2 className="text-base font-semibold text-ink">Election Commission Structure</h2>
            </div>
            <div className="flex items-center gap-2 text-ink">
              <button
                type="button"
                aria-label="Columns view"
                className="flex size-8 items-center justify-center rounded-lg hover:bg-brand-shade"
              >
                <ColumnsIcon className="h-[11px] w-[15px]" />
              </button>
              <button
                type="button"
                aria-label="More options"
                className="flex size-8 items-center justify-center rounded-lg hover:bg-brand-shade"
              >
                <KebabIcon className="h-[13px] w-[3px]" />
              </button>
            </div>
          </div>

          {/* Breadcrumb + columns (miller view) */}
          <DestinationColumns
            tree={destTree}
            branches={branches}
            mapping={mapping}
            setMapping={setMapping}
          />

          {/* Footer */}
          <div className="flex justify-end border-t border-line bg-white px-4 py-4">
            <button
              type="button"
              onClick={onConfirm}
              className="flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-xs font-semibold tracking-[0.24px] text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              Confirm Selection
              <ArrowRightIcon className="h-[9px] w-[16px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
